import { projectStudyIncomingDtoSchema } from '@core/services/study-table/study-table.effects';
import { defaultEntityState, IEntityState } from '@core/state/entities/entity';
import {
  mergeEntityState,
  normalizeAndMergeEntity,
} from '@core/state/entities/entity.helpers';
import { IStudy, StudyChildKeys } from './study.entity';
import {
  incompleteStudiesPayloadSchema,
  stageActions,
  stageStudyActions,
} from '@core/actions/stage.actions';
import { studyOutgoingDtoSchema } from '@core/services/stage/review-effects';
import { INormalizedEntity, IStudyChild } from '../entity';
import * as _ from 'lodash-es';
import { normalize } from 'normalizr';
import { entityKey } from '../entity-name-map';
import { studiesActions } from '@app/core/actions/studies.actions';
import { createReducer, on } from '@ngrx/store';

export const childIdsToStudy = <
  TEntity extends IStudyChild,
  TKey extends StudyChildKeys,
>(
  key: TKey,
  state: IEntityState<IStudy>,
  normalizedPayload: INormalizedEntity<TKey>,
  remove?: boolean
): IEntityState<IStudy> => {
  // check for multiple results (just get first if array)
  // get studyId

  const values = normalizedPayload.entities[key];

  const items = _.values(values) as IStudyChild[];

  // If there's not at least 1 item then pointless to continue so return
  if (!items[0]) {
    return state;
  }

  const studyId = items[0].studyId;
  const study = state.byId[studyId];
  const itemKeys = _.keys(values);
  const operation = !remove ? _.union : _.difference;
  return {
    ...state,
    ...{
      byId: {
        ...state.byId,
        [study.id]: {
          ...study,
          ...{
            [key]: operation(study[key], itemKeys),
          },
        },
      },
    },
  };
};

export const studyEntityReducer = createReducer(
  defaultEntityState<IStudy>(),
  on(studiesActions.studyListUpdated, (state, props) =>
    normalizeAndMergeEntity(
      entityKey.studies,
      state,
      props,
      projectStudyIncomingDtoSchema
    )
  ),
  on(stageActions.receivedInProgressStudies, (state, props) =>
    mergeEntityState(entityKey.studies, state, props)
  ),
  on(stageActions.incompleteSessionsUpdated, (state, props) =>
    normalizeAndMergeEntity(
      entityKey.studies,
      state,
      props,
      incompleteStudiesPayloadSchema
    )
  ),
  on(stageStudyActions.receivedStageStudy, (state, props) =>
    mergeEntityState(entityKey.studies, state, props)
  ),
  on(stageActions.updateSession, (state, props) =>
    childIdsToStudy(entityKey.sessions, state, {
      ...props,
      result: props.sessionId,
    } as INormalizedEntity<StudyChildKeys>)
  ),
  on(stageActions.sessionSaved, (state, props) =>
    props.correlationId !== undefined
      ? childIdsToStudy(
          entityKey.sessions,
          state,
          props as INormalizedEntity<StudyChildKeys>
        )
      : state
  ),
  on(stageActions.saveSession, (state, props) =>
    props.correlationId !== undefined
      ? state
      : childIdsToStudy(
          entityKey.sessions,
          state,
          normalize(props, studyOutgoingDtoSchema)
        )
  ),
  on(stageActions.deleteSession, (state, props) =>
    props.correlationId !== undefined
      ? state
      : childIdsToStudy(
          entityKey.sessions,
          state,
          normalize(props, studyOutgoingDtoSchema),
          true
        )
  ),
  on(stageActions.commitSessionDeletion, (state, props) =>
    childIdsToStudy(
      entityKey.sessions,
      state,
      normalize(props, studyOutgoingDtoSchema),
      true
    )
  )
);
